import { API_ORIGIN, backendPrOverride } from './apiBase';

export const SALE_PATH = '/newsale';
export const LOGIN_PATH = '/login';
const POS_TYPE_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;
const MAX_POS_TYPE_INPUT_LENGTH = 80;
const POS_HEADER = 'X-Krece-POS-Type';

export type EmbeddedSaleContext = { pos_type: string | null };
export type SaleOriginPayload = { embedded: true | null; pos_type: string | null };
type FlowLocation = Pick<Location, 'pathname' | 'search'>;

export function normalize_route_path(pathname: string): string {
	return pathname.replace(/\/+$/, '').toLowerCase() || '/';
}

export function normalize_pos_type(value: string | null): string | null {
	// Bound the untrusted label before normalization and validation. It never grants access.
	if (!value || value.length > MAX_POS_TYPE_INPUT_LENGTH) return null;
	const normalized = value.trim().toLowerCase();
	return POS_TYPE_PATTERN.test(normalized) ? normalized : null;
}

export function get_embedded_sale_context(location: FlowLocation = window.location): EmbeddedSaleContext | null {
	const path = normalize_route_path(location.pathname);
	if (path !== SALE_PATH && path !== LOGIN_PATH) return null;
	const params = new URLSearchParams(location.search);
	if (params.get('embed') !== '1') return null;
	return { pos_type: normalize_pos_type(params.get('pos_type')) };
}

export function get_sale_origin_payload(location: FlowLocation = window.location): SaleOriginPayload {
	const context = get_embedded_sale_context(location);
	return { embedded: context ? true : null, pos_type: context?.pos_type ?? null };
}

export function embedded_sale_url(path: typeof SALE_PATH | typeof LOGIN_PATH, context: EmbeddedSaleContext): string {
	const params = new URLSearchParams({ embed: '1' });
	const pos_type = normalize_pos_type(context.pos_type);
	if (pos_type) params.set('pos_type', pos_type);
	// Keep a selected preview backend across login redirects and refreshes.
	const backend_pr = backendPrOverride();
	if (backend_pr) params.set('backendPr', backend_pr);
	return `${path}?${params.toString()}`;
}

export function without_pos_parameters(search: string): string {
	const params = new URLSearchParams(search);
	params.delete('embed');
	params.delete('pos_type');
	const result = params.toString();
	return result ? `?${result}` : '';
}

export function get_pos_headers(endpoint: string | URL): Record<string, string> {
	const context = get_embedded_sale_context();
	if (!context) return {};
	try {
		const destination = new URL(String(endpoint), `${API_ORIGIN}/`);
		if (destination.origin !== new URL(API_ORIGIN).origin) return {};
		return { [POS_HEADER]: context.pos_type ?? 'generic' };
	} catch {
		return {};
	}
}
